import * as React from 'react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 relative',
        month: 'space-y-4 w-full !ml-0',
        month_caption: 'flex justify-center pt-1 relative items-center',
        caption_label: '2xl-text font-bold leading-7 z-[1]',
        nav: 'space-x-1 flex items-center text-black absolute z-0 w-full justify-between w-full h-8 top-1 z-[1]',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'h-8 w-8 bg-transparent p-0 opacity-100 rounded-full border border-white hover:border-black rounded-full hover:bg-white focus:bg-white active:bg-white active:border-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'absolute left-1 border-0 hover-border-0 before:bg-white before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-bounce hover:before:ease-bounce before:transition-all hover:before:transition-all hover:before:h-full before:border before:border-black hover:before:w-full before:z-[-1]',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'h-8 w-8 bg-transparent p-0 opacity-100 rounded-full border border-white hover:border-black rounded-full hover:bg-white focus:bg-white active:bg-white active:border-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'absolute right-1 border-0 hover-border-0 before:bg-white before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-bounce hover:before:ease-bounce before:transition-all hover:before:transition-all hover:before:h-full before:border before:border-black hover:before:w-full before:z-[-1]',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex justify-center',
        weekday:
          'text-red-500 rounded-full max-[375px]:w-8 max-[375px]:text-sm w-12 h-6 font-medium leading-5 text-base hover:rounded-full',
        week: 'flex w-full mt-2 justify-center',
        day: 'max-[375px]:w-8 max-[375px]:h-8 max-[375px]:text-sm w-12	h-12 text-center text-base p-0 relative border-black rounded-full hover:rounded-full [&:has([aria-selected].day-range-end)]:rounded-full [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-full last:[&:has([aria-selected])]:rounded-full focus-within:relative focus-within:z-20',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'relative max-[375px]:w-8 max-[375px]:h-8 max-[375px]:text-sm w-12 h-12 z-10 p-0 font-normal text-base aria-selected:opacity-100 rounded-full before:bg-accent before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-in before:hover:ease-bounce before:transition-all hover:before:h-full hover:before:w-full before:z-[-1]',
        ),
        range_end: 'day-range-end',
        selected:
          'rounded-full hover:rounded-full bg-green-500 leading-5 text-base text-black font-medium hover:bg-green-500 hover:text-black focus:bg-green-500 focus:text-black before:bg-green-500 before:absolute before:rounded-full before:scale-100 hover:before:scale-[1.15] before:ease-bounce hover:before:ease-bounce before:transition-all hover:before:transition-all hover:before:h-full hover:before:w-full before:z-[-1]',
        today:
          'leading-5 rounded-full text-base text-black border border-black font-medium hover:rounded-full',
        outside:
          'day-outside rounded-full text-base hover:rounded-full text-zinc-400 aria-selected:bg-slate-100/50 aria-selected:text-zinc-400 aria-selected:opacity-30 [&>button]:leading-5 [&>button]:text-base [&>button]:text-zinc-400 [&>button]:border-zinc-200',
        disabled:
          '[&>button]:leading-5 [&>button]:text-base [&>button]:text-zinc-400 [&>button]:border-zinc-200',
        range_middle: 'aria-selected:bg-slate-100 aria-selected:text-slate-900',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: (props) => (
          <img
            className='h-4'
            src={
              props.orientation == 'left'
                ? '/images/arrow-left-black.svg'
                : '/images/arrow-right-black.svg'
            }
            alt={props.orientation == 'left' ? 'Left arrow image' : 'Right arrow image'}
            title=''
          />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
